import { SeoLandingLayout, SeoLandingPageData } from '../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'Logiciel de prévision de trésorerie : projeter, simuler, anticiper | Trezoone',
    description: 'Choisissez le bon logiciel de prévision de trésorerie. Trezoone connecte vos données bancaires à vos prévisions pour une vision cash sur 12 mois.',
    canonical: '/logiciel-prevision-tresorerie',
    keywords: 'logiciel prévision trésorerie, logiciel prévisionnel trésorerie, logiciel cash flow prévisionnel, outil prévision trésorerie',
  },
  hero: {
    tag: 'Logiciel prévision trésorerie',
    title: 'Un logiciel de prévision de trésorerie qui parle le langage du dirigeant.',
    subtitle: 'Pas de jargon. Pas de formation de 3 jours. Juste : votre solde dans 3 mois, 6 mois, 12 mois. Et une alerte quand ça risque de coincer.',
    ctaLabel: 'Essayer gratuitement',
  },
  pilierLink: {
    label: 'Guide : prévision de trésorerie',
    href: '/prevision-tresorerie',
  },
  sections: [
    {
      type: 'hook',
      title: 'Pourquoi les logiciels de prévision existants ne conviennent pas aux petites structures',
      content: 'Les logiciels de trésorerie "pro" sont pensés pour les grandes entreprises avec des DAF. Pour une TPE, PME ou association :',
      items: [
        'Trop complexes : interfaces pensées pour des financiers, pas pour des dirigeants',
        'Trop chers : 300 à 2 000 €/mois pour des fonctionnalités inutilisées',
        'Pas connectés : nécessitent une intégration ERP longue et coûteuse',
        'Trop rigides : modèles imposés, pas de flexibilité pour votre situation spécifique',
      ],
    },
    {
      type: 'explain',
      title: 'Ce qu\'on attend d\'un logiciel de prévision de trésorerie',
      content: 'Un bon logiciel de prévision doit répondre à 3 questions fondamentales :',
      items: [
        '"Est-ce que j\'aurai assez de cash pour payer mes charges le mois prochain ?"',
        '"Si une recette importante est retardée de 2 mois, comment j\'absorbe le choc ?"',
        '"Quelle est la situation dans 6 mois si mon activité progresse de 20 % ?"',
      ],
    },
    {
      type: 'solution',
      title: 'Trezoone répond à ces 3 questions',
      content: 'Trezoone est construit autour de la prévision de trésorerie. Pas comme une fonctionnalité parmi d\'autres, mais comme son cœur.',
      items: [
        'Connexion bancaire ou import CSV/OFX pour les données réelles',
        'Saisie simple des prévisions : recettes attendues, charges prévues',
        'Calcul automatique du solde mois par mois sur 12 mois',
        'Simulation de scénarios en quelques clics',
        'Alertes configurables quand le solde prévisionnel devient critique',
        'Dashboard visuel avec courbe de trésorerie et zones de tension',
        'Export des prévisions en PDF pour vos partenaires financiers',
      ],
    },
    {
      type: 'midcta',
      title: 'Répondez à "Est-ce que je tiens le mois prochain ?" en 5 minutes',
      content: 'Essai gratuit 14 jours · Prévision 12 mois · Scénarios inclus',
    },
    {
      type: 'faq',
      title: 'Questions fréquentes',
      faq: [
        {
          q: 'Quelle est la différence entre un logiciel de trésorerie et un logiciel de prévision ?',
          a: 'Un logiciel de trésorerie gère les flux passés et présents. Un logiciel de prévision projette les flux futurs. Trezoone fait les deux : il analyse votre historique ET projette votre avenir.',
        },
        {
          q: 'Le logiciel fonctionne-t-il sans données bancaires ?',
          a: 'Oui. Vous pouvez commencer avec uniquement votre solde actuel et vos prévisions. La connexion bancaire enrichit l\'outil mais n\'est pas obligatoire.',
        },
        {
          q: 'Les prévisions peuvent-elles être partagées avec un expert-comptable ?',
          a: 'Oui. Vous pouvez exporter les prévisions en PDF ou CSV, et inviter votre comptable à accéder en lecture à votre espace Trezoone.',
        },
      ],
    },
    {
      type: 'cluster',
      title: 'Prévision et logiciels : explorer les ressources',
      links: [
        { label: 'Outil prévision trésorerie', href: '/outil-prevision-tresorerie' },
        { label: 'Prévision de trésorerie', href: '/prevision-tresorerie' },
        { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
        { label: 'Logiciel de trésorerie', href: '/logiciel-tresorerie' },
        { label: 'Simulation trésorerie association', href: '/simulation-tresorerie-association' },
        { label: 'Cash flow prévisionnel', href: '/cash-flow-entreprise' },
        { label: 'Stress test trésorerie', href: '/stress-test-tresorerie' },
      ],
    },
    {
      type: 'cta',
      title: 'La prévision de trésorerie, enfin accessible à tous',
      content: 'Essai 14 jours gratuit. Opérationnel en 30 minutes.',
    },
  ],
  relatedLinks: [
    { label: 'Prévision trésorerie', href: '/prevision-tresorerie' },
    { label: 'Logiciel trésorerie', href: '/logiciel-tresorerie' },
    { label: 'Gestion trésorerie', href: '/gestion-tresorerie' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }

export default function LogicielPrevisionTresoreriePage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
